import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import PasswordFields, { validatePasswordPair } from "./PasswordFields";

interface PasswordUserDialogProps {
  /** Set when changing an existing account's password; undefined when creating a new one. */
  username?: string;
  /** The existing account's enabled flag, preserved across a password change. */
  currentEnabled?: boolean;
  existingUsernames: string[];
  onCreate: (username: string, password: string, enabled: boolean) => Promise<boolean>;
  onChangePassword: (
    username: string,
    password: string,
    enabled: boolean
  ) => Promise<boolean>;
  close(): void;
}

const PasswordUserDialog = ({
  username,
  currentEnabled,
  existingUsernames,
  onCreate,
  onChangePassword,
  close,
}: PasswordUserDialogProps) => {
  const isChangePasswordMode = username !== undefined;

  const [newUsername, setNewUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [enabled, setEnabled] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleSave = async () => {
    setError("");

    const passwordError = validatePasswordPair(password, confirmPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (isChangePasswordMode) {
      setIsSaving(true);
      const ok = await onChangePassword(
        username as string,
        password,
        currentEnabled ?? true
      );
      setIsSaving(false);
      if (ok) close();
      return;
    }

    const trimmed = newUsername.trim();
    if (!trimmed) {
      setError("Username is required.");
      return;
    }
    if (existingUsernames.includes(trimmed)) {
      setError(`User "${trimmed}" already exists.`);
      return;
    }

    setIsSaving(true);
    const ok = await onCreate(trimmed, password, enabled);
    setIsSaving(false);
    if (ok) close();
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={close}
        disabled={isSaving}
      />
      <Button
        label={isChangePasswordMode ? "Change password" : "Add user"}
        icon={isChangePasswordMode ? "pi pi-key" : "pi pi-plus"}
        onClick={handleSave}
        loading={isSaving}
      />
    </div>
  );

  return (
    <Dialog
      visible
      modal
      onHide={close}
      header={
        isChangePasswordMode ? `Change password — ${username}` : "Add OPC UA user"
      }
      style={{ width: "min(460px, 95vw)" }}
      footer={footer}
    >
      <div className="p-fluid flex flex-column gap-3">
        {!isChangePasswordMode && (
          <div className="p-field flex flex-column gap-2">
            <label htmlFor="opcUaUsername">Username *</label>
            <InputText
              id="opcUaUsername"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              disabled={isSaving}
              autoComplete="off"
            />
          </div>
        )}

        <PasswordFields
          password={password}
          confirmPassword={confirmPassword}
          setPassword={setPassword}
          setConfirmPassword={setConfirmPassword}
          disabled={isSaving}
        />

        {!isChangePasswordMode && (
          <div className="p-field flex flex-row align-items-center gap-2">
            <InputSwitch
              id="opcUaUserEnabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.value as boolean)}
              className="toggle-button-custom"
              disabled={isSaving}
            />
            <label htmlFor="opcUaUserEnabled">Enabled</label>
          </div>
        )}

        {error && <small className="p-error">{error}</small>}
      </div>
    </Dialog>
  );
};

export default PasswordUserDialog;
